/**
 * screens-inventory.ts — ซื้อสินค้าและสต็อก (T093)
 *
 * - ต้นทุนซื้อจริง (accounting) = มูลค่า line + ค่าขนส่งเข้า + ค่าบรรจุหีบห่อ + ต้นทุนบริการ
 *   แยกจากต้นทุนมาตรฐาน (management) ไม่เขียนทับกัน
 * - movement: รับเข้า/โอน/ปรับยอด/เสียหาย/ของแถม/ต้นงวด/ปลายงวด + ยอดคงเหลือ
 * - ตรวจนับ: variance = นับได้ − ระบบ
 * - กระทบยอด COGS: standard vs accounting คนละช่อง พร้อมผลต่าง
 */
import { signedMovementQty, type InventoryMovementType } from '@rrp/shared';
import type { Page } from './router.js';
import { getTenant } from './state.js';
import * as api from './api.js';
import {
  esc,
  money,
  pageShell,
  dataTable,
  val,
  opt,
  bindSubmit,
  errorBox,
  drawerHtml,
} from './ui.js';

type Row = Record<string, unknown>;

const MOVEMENT_TYPES: [string, string][] = [
  ['receive', 'รับเข้า'],
  ['opening', 'ยอดต้นงวด'],
  ['transfer', 'โอน'],
  ['adjust', 'ปรับยอด'],
  ['damage_loss', 'เสียหาย/สูญหาย'],
  ['giveaway', 'ของแถม'],
  ['closing', 'ยอดปลายงวด (snapshot)'],
];
const MOVEMENT_LABELS = Object.fromEntries(MOVEMENT_TYPES);

export const inventoryPage: Page = {
  title: 'ซื้อสินค้าและสต็อก',
  render: () =>
    pageShell(
      'ซื้อสินค้าและสต็อก',
      `<section class="card">
        <h3>บันทึกการซื้อสินค้า (ต้นทุนบัญชี)</h3>
        <form id="purchase-form" class="grid-form">
          <label>ผู้ขาย<input name="supplierName" placeholder="ชื่อซัพพลายเออร์"></label>
          <label>เลขที่เอกสาร<input name="documentNo" placeholder="INV-001"></label>
          <label>วันที่ซื้อ<input name="purchaseDate" type="date"></label>
          <label>SKU ID<input name="skuId" placeholder="uuid ของ SKU"></label>
          <label>จำนวน<input name="quantity" inputmode="decimal" placeholder="100" required></label>
          <label>ต้นทุนซื้อ/หน่วย<input name="unitCost" inputmode="decimal" placeholder="9.50" required></label>
          <label>ค่าขนส่งเข้า<input name="freightIn" inputmode="decimal" placeholder="0.00"></label>
          <label>ค่าบรรจุหีบห่อ<input name="packagingCost" inputmode="decimal" placeholder="0.00"></label>
          <label>ต้นทุนบริการ<input name="serviceCost" inputmode="decimal" placeholder="0.00"></label>
          <div class="form-actions"><button type="submit">บันทึกการซื้อ</button><span id="purchase-msg" class="muted"></span></div>
        </form>
        <div id="purchase-list">กำลังโหลด…</div>
      </section>

      <section class="card">
        <h3>ความเคลื่อนไหวสต็อก</h3>
        <form id="movement-form" class="grid-form">
          <label>SKU ID<input name="skuId" placeholder="uuid ของ SKU" required></label>
          <label>ประเภท<select name="movementType">${MOVEMENT_TYPES.map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}</select></label>
          <label>จำนวน<input name="quantity" inputmode="decimal" placeholder="10" required></label>
          <label>ต้นทุน/หน่วย<input name="unitCost" inputmode="decimal" placeholder="9.50"></label>
          <label>วันที่<input name="movementDate" type="date"></label>
          <label>คลัง<input name="warehouse" placeholder="คลังหลัก"></label>
          <label>อ้างอิง<input name="reference" placeholder="เลขที่เอกสาร"></label>
          <div class="form-actions"><button type="submit">บันทึกความเคลื่อนไหว</button><span id="movement-msg" class="muted"></span></div>
        </form>
        <form id="balance-form" class="grid-form">
          <label>ดูยอดคงเหลือของ SKU<input name="skuId" placeholder="uuid ของ SKU" required></label>
          <div class="form-actions"><button type="submit">ดูยอดคงเหลือ</button><span id="balance-msg" class="muted"></span></div>
        </form>
        <div id="movement-list">กำลังโหลด…</div>
      </section>

      <section class="card">
        <h3>ตรวจนับสินค้า</h3>
        <form id="count-form" class="grid-form">
          <label>SKU ID<input name="skuId" placeholder="uuid ของ SKU" required></label>
          <label>วันที่ตรวจนับ<input name="countDate" type="date"></label>
          <label>นับได้จริง<input name="countedQty" inputmode="decimal" required></label>
          <label>ยอดตามระบบ<input name="systemQty" inputmode="decimal" required></label>
          <label>คลัง<input name="warehouse" placeholder="คลังหลัก"></label>
          <div class="form-actions"><button type="submit">บันทึกการตรวจนับ</button><span id="count-msg" class="muted"></span></div>
        </form>
        <div id="count-list">กำลังโหลด…</div>
      </section>

      <section class="card">
        <h3>กระทบยอดต้นทุน (Standard vs Accounting)</h3>
        <form id="recon-form" class="grid-form">
          <label>ปี<input name="periodYear" type="number" value="2025" required></label>
          <label>เดือน<input name="periodMonth" type="number" min="1" max="12" value="4"></label>
          <label>ต้นทุนมาตรฐาน (Dashboard)<input name="standardCogs" inputmode="decimal" required></label>
          <label>ต้นทุนบัญชี (ซื้อจริง/สต็อก)<input name="accountingCogs" inputmode="decimal" required></label>
          <label>หมายเหตุ<input name="notes" placeholder="อธิบายผลต่าง"></label>
          <div class="form-actions"><button type="submit">บันทึกการกระทบยอด</button><span id="recon-msg" class="muted"></span></div>
        </form>
        <div id="recon-list">กำลังโหลด…</div>
      </section>
      ${drawerHtml}`,
    ),
  mount(root) {
    if (!getTenant()) return;
    const el = (id: string): HTMLElement => root.querySelector<HTMLElement>(`#${id}`)!;
    /** id → รหัส SKU เพื่อแสดงชื่อที่อ่านออกแทน uuid */
    const skuCodes = new Map<string, string>();
    const loadSkuCodes = async (): Promise<void> => {
      try {
        const { items } = await api.listSkus({ limit: 200 });
        for (const s of items) skuCodes.set(s.id, s.skuCode);
      } catch {
        // ไม่มีสิทธิ์/ยังไม่มี SKU → แสดง id ตามเดิม
      }
    };
    const skuLabel = (id: unknown): string => skuCodes.get(String(id)) ?? String(id ?? '—');

    const loadPurchases = async (): Promise<void> => {
      try {
        const { items } = await api.listPurchases();
        el('purchase-list').innerHTML = dataTable(
          ['วันที่', 'ผู้ขาย', 'เอกสาร', 'มูลค่าสินค้า', 'ขนส่ง/แพ็ก/บริการ', 'ต้นทุนบัญชีรวม'],
          items.map((r: Row) => {
            // DB เก็บ total_amount = ต้นทุนบัญชีรวม (lines + freight + packaging + service)
            const extras =
              Number(r['freight_in'] ?? 0) +
              Number(r['packaging_cost'] ?? 0) +
              Number(r['service_cost'] ?? 0);
            const accounting = Number(r['total_amount'] ?? 0);
            return {
              cells: [
                esc(r['purchase_date'] ?? '—'),
                esc(r['supplier_name'] ?? '—'),
                esc(r['document_no'] ?? '—'),
                money(accounting - extras),
                money(extras),
                `<strong>${money(accounting)}</strong>`,
              ],
            };
          }),
          'ยังไม่มีรายการซื้อ',
        );
      } catch (e) {
        el('purchase-list').innerHTML = errorBox(e);
      }
    };

    const loadMovements = async (): Promise<void> => {
      try {
        const { items } = await api.listMovements();
        el('movement-list').innerHTML = dataTable(
          ['วันที่', 'SKU', 'ประเภท', 'จำนวน', 'ผลต่อยอด', 'คลัง', 'อ้างอิง'],
          items.map((r: Row) => {
            const type = String(r['movement_type']);
            // ทิศทางต่อยอดคงเหลือใช้กติกาเดียวกับ backend (shared)
            const signed = Number(
              signedMovementQty(type as InventoryMovementType, Number(r['quantity'] ?? 0)),
            );
            return {
              cells: [
                esc(r['movement_date'] ?? '—'),
                esc(skuLabel(r['sku_id'])),
                esc(MOVEMENT_LABELS[type] ?? type),
                esc(r['quantity']),
                `<span class="num ${signed < 0 ? 'neg-num' : ''}">${signed > 0 ? '+' : ''}${esc(signed)}</span>`,
                esc(r['warehouse'] ?? '—'),
                esc(r['reference'] ?? '—'),
              ],
            };
          }),
          'ยังไม่มีความเคลื่อนไหว',
        );
      } catch (e) {
        el('movement-list').innerHTML = errorBox(e);
      }
    };

    const loadCounts = async (): Promise<void> => {
      try {
        const { items } = await api.listCounts();
        el('count-list').innerHTML = dataTable(
          ['วันที่', 'SKU', 'นับได้', 'ตามระบบ', 'ผลต่าง', 'คลัง'],
          items.map((r: Row) => {
            const variance = Number(r['variance'] ?? 0);
            return {
              cls: variance !== 0 ? 'row-warn' : undefined,
              cells: [
                esc(r['count_date'] ?? '—'),
                esc(skuLabel(r['sku_id'])),
                esc(r['counted_qty']),
                esc(r['system_qty']),
                `<span class="num ${variance < 0 ? 'neg-num' : ''}">${esc(variance)}</span>`,
                esc(r['warehouse'] ?? '—'),
              ],
            };
          }),
          'ยังไม่มีการตรวจนับ',
        );
      } catch (e) {
        el('count-list').innerHTML = errorBox(e);
      }
    };

    const loadRecon = async (): Promise<void> => {
      try {
        const { items } = await api.listCogsRecon();
        el('recon-list').innerHTML = dataTable(
          ['งวด', 'ต้นทุนมาตรฐาน', 'ต้นทุนบัญชี', 'ผลต่าง', 'หมายเหตุ'],
          items.map((r: Row) => {
            const variance = Number(r['variance'] ?? 0);
            return {
              cells: [
                `${esc(r['period_year'])}${r['period_month'] ? `-${String(r['period_month']).padStart(2, '0')}` : ''}`,
                money(r['standard_cogs']),
                money(r['accounting_cogs']),
                `<span class="num ${variance < 0 ? 'neg-num' : ''}">${money(variance)}</span>`,
                esc(r['notes'] ?? '—'),
              ],
            };
          }),
          'ยังไม่มีการกระทบยอด',
        );
      } catch (e) {
        el('recon-list').innerHTML = errorBox(e);
      }
    };

    bindSubmit(
      root.querySelector<HTMLFormElement>('#purchase-form')!,
      el('purchase-msg'),
      async (f) => {
        const line: Row = {
          quantity: Number(val(f, 'quantity')),
          unitCost: val(f, 'unitCost'),
        };
        const skuId = opt(f, 'skuId');
        if (skuId) line['skuId'] = skuId;
        const body: Row = { lines: [line] };
        for (const k of ['supplierName', 'documentNo', 'purchaseDate']) {
          const v = opt(f, k);
          if (v) body[k] = v;
        }
        for (const k of ['freightIn', 'packagingCost', 'serviceCost']) {
          const v = opt(f, k);
          if (v) body[k] = v;
        }
        await api.createPurchase(body);
      },
      loadPurchases,
    );

    bindSubmit(
      root.querySelector<HTMLFormElement>('#movement-form')!,
      el('movement-msg'),
      async (f) => {
        const body: Row = {
          skuId: val(f, 'skuId'),
          movementType: val(f, 'movementType'),
          quantity: Number(val(f, 'quantity')),
        };
        for (const k of ['unitCost', 'movementDate', 'warehouse', 'reference']) {
          const v = opt(f, k);
          if (v) body[k] = v;
        }
        await api.createMovement(body);
      },
      loadMovements,
    );

    bindSubmit(
      root.querySelector<HTMLFormElement>('#balance-form')!,
      el('balance-msg'),
      async (f) => {
        const skuId = val(f, 'skuId');
        const { balance } = await api.stockBalance(skuId);
        el('balance-msg').className = 'muted';
        el('balance-msg').textContent = `ยอดคงเหลือของ ${skuLabel(skuId)}: ${balance}`;
      },
      undefined,
      { reset: false, quiet: true },
    );

    bindSubmit(
      root.querySelector<HTMLFormElement>('#count-form')!,
      el('count-msg'),
      async (f) => {
        const body: Row = {
          skuId: val(f, 'skuId'),
          countedQty: Number(val(f, 'countedQty')),
          systemQty: Number(val(f, 'systemQty')),
        };
        for (const k of ['countDate', 'warehouse']) {
          const v = opt(f, k);
          if (v) body[k] = v;
        }
        await api.createCount(body);
      },
      loadCounts,
    );

    bindSubmit(
      root.querySelector<HTMLFormElement>('#recon-form')!,
      el('recon-msg'),
      async (f) => {
        const body: Row = {
          periodYear: Number(val(f, 'periodYear')),
          standardCogs: val(f, 'standardCogs'),
          accountingCogs: val(f, 'accountingCogs'),
        };
        const m = opt(f, 'periodMonth');
        if (m) body['periodMonth'] = Number(m);
        const notes = opt(f, 'notes');
        if (notes) body['notes'] = notes;
        await api.createCogsRecon(body);
      },
      loadRecon,
    );

    void loadSkuCodes().then(() => {
      void loadMovements();
      void loadCounts();
    });
    void loadPurchases();
    void loadMovements();
    void loadCounts();
    void loadRecon();
  },
};
